// src/components/dashboard/settings/CourseManagement.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";

// Guaranteed Core MUI Icons
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CastForEducationRoundedIcon from "@mui/icons-material/CastForEducationRounded";

const CourseManagement = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Search & Dialog States
  const [searchQuery, setSearchQuery] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // New Course Modal State
  const [newCourse, setNewCourse] = useState({
    title: "",
    instructor: "",
    category: "Clinical Protocol",
    certificateId: "",
  });

  // Authentic Healthcare Staff Training & Continuing Medical Education (CME) Courses
  const [courses, setCourses] = useState([
    {
      id: 1,
      title: "HIPAA & Patient Data Privacy Compliance",
      instructor: "Dr. Usman Rehman",
      created: "7/18/2026",
      certificateId: "CERT-HIPAA-2026",
      category: "Compliance",
      enrolled: 48,
      gradient: "linear-gradient(135deg, #112D47 0%, #1F4E79 100%)",
    },
    {
      id: 2,
      title: "Advanced Stroke & Neuro Assessment (NIHSS)",
      instructor: "Dr. Sarah Jenkins",
      created: "8/04/2026",
      certificateId: "CERT-NIHSS-881",
      category: "Neurology",
      enrolled: 32,
      gradient: "linear-gradient(135deg, #173E62 0%, #1E5E5F 100%)",
    },
    {
      id: 3,
      title: "Electronic Visit Verification (EVV) Field Protocols",
      instructor: "awais09",
      created: "8/22/2026",
      certificateId: "CERT-EVV-404",
      category: "Caregiver Training",
      enrolled: 64,
      gradient: "linear-gradient(135deg, #1E5E5F 0%, #057A44 100%)",
    },
    {
      id: 4,
      title: "Quality Assurance & Patient Safety Protocols",
      instructor: "Dr. Bilal",
      created: "7/18/2026",
      certificateId: "CERT-QA-102",
      category: "General Clinical",
      enrolled: 18,
      gradient: "linear-gradient(135deg, #0B2540 0%, #245E65 100%)",
    },
  ]);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";

  const handleDelete = (id) => {
    setCourses((prev) => prev.filter((c) => c.id !== id));
    setToastMessage("Course deleted successfully.");
  };

  const handleCreate = () => {
    if (!newCourse.title.trim() || !newCourse.instructor.trim()) return;
    const nextId = Math.max(...courses.map((c) => c.id), 0) + 1;
    const now = new Date();
    const createdDate = `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;

    setCourses([
      {
        id: nextId,
        title: newCourse.title,
        instructor: newCourse.instructor,
        created: createdDate,
        certificateId: newCourse.certificateId || `CERT-${Math.floor(1000 + Math.random() * 9000)}`,
        category: newCourse.category,
        enrolled: 1,
        gradient: "linear-gradient(135deg, #112D47 0%, #1E5E5F 100%)",
      },
      ...courses,
    ]);

    setNewCourse({
      title: "",
      instructor: "",
      category: "Clinical Protocol",
      certificateId: "",
    });
    setOpenModal(false);
    setToastMessage("New course published successfully!");
  };

  // Filter Search
  const filteredCourses = courses.filter(
    (c) =>
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.instructor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.certificateId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", pb: 6, boxSizing: "border-box" }}>
      {/* 1. TOP CONTROLLER & SEARCH BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "center" },
            justifyContent: "space-between",
            gap: 2.5,
          }}
        >
          {/* Left Title & Book Icon Badge */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: tealMain,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MenuBookRoundedIcon sx={{ fontSize: "1.7rem" }} />
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "1.2rem", md: "1.35rem" },
                    fontWeight: 800,
                    color: primaryTextColor,
                    letterSpacing: "-0.3px",
                    lineHeight: 1.2,
                  }}
                >
                  Course Management
                </Typography>
                <Chip
                  label="CME & Training"
                  size="small"
                  sx={{
                    height: "22px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    backgroundColor: pastelTealBg,
                    color: tealButton,
                    borderRadius: "6px",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: secondaryTextColor,
                  fontWeight: 500,
                  mt: 0.3,
                }}
              >
                Manage continuing medical education, staff compliance modules, and clinical certifications
              </Typography>
            </Box>
          </Box>

          {/* Right Section: Certificate ID Search + Add Course Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            <TextField
              size="small"
              placeholder="Search certificate ID or course..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SchoolRoundedIcon sx={{ color: tealButton, fontSize: "1.15rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: "280px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: "0.85rem",
                  color: primaryTextColor,
                  backgroundColor: controlBg,
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />

            <Button
              variant="contained"
              onClick={() => setOpenModal(true)}
              startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
              sx={{
                height: "40px",
                backgroundColor: addGreen,
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: "0.82rem",
                borderRadius: "6px",
                px: 2.5,
                whiteSpace: "nowrap",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#046337",
                  boxShadow: "none",
                },
              }}
            >
              Add Course
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 2. SUB-HEADING: All Courses (Count) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5, px: 0.5 }}>
        <MenuBookRoundedIcon sx={{ color: tealButton, fontSize: "1.15rem" }} />
        <Typography sx={{ fontSize: "0.98rem", fontWeight: 800, color: primaryTextColor, letterSpacing: "-0.2px" }}>
          All Courses ({filteredCourses.length})
        </Typography>
      </Box>

      {/* 3. FULL-WIDTH RESPONSIVE CARD GRID */}
      {filteredCourses.length === 0 ? (
        <Box
          sx={{
            p: 8,
            textAlign: "center",
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1px dashed ${softCardBorder}`,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <CastForEducationRoundedIcon sx={{ fontSize: "2.6rem", color: secondaryTextColor, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ color: secondaryTextColor, fontWeight: 600, fontSize: "0.88rem" }}>
            No courses found matching your criteria.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: "20px",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {filteredCourses.map((course) => (
            <Box
              key={course.id}
              sx={{
                backgroundColor: cardBg,
                borderRadius: "16px",
                border: `1px solid ${softCardBorder}`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
                boxShadow: isDark
                  ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                  : "0 2px 10px rgba(10, 37, 64, 0.02)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  borderColor: tealMain,
                  boxShadow: isDark
                    ? "0 10px 30px rgba(0, 0, 0, 0.5)"
                    : "0 8px 24px rgba(30, 94, 95, 0.1)",
                },
              }}
            >
              {/* Clinical Gradient Top Header */}
              <Box
                sx={{
                  height: 145,
                  width: "100%",
                  background: course.gradient,
                  p: 2.2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Chip
                    label={course.category}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(6px)",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      borderRadius: "6px",
                    }}
                  />
                  <Chip
                    label={course.certificateId}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(0, 0, 0, 0.35)",
                      color: "#FFFFFF",
                      fontFamily: "monospace",
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      borderRadius: "6px",
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CastForEducationRoundedIcon sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "1.6rem" }} />
                  <Typography sx={{ color: "#FFFFFF", fontSize: "0.78rem", fontWeight: 700 }}>
                    {course.enrolled} Enrolled Clinicians
                  </Typography>
                </Box>
              </Box>

              {/* Card Body Details */}
              <Box
                sx={{
                  p: 2.2,
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.98rem",
                      fontWeight: 800,
                      color: primaryTextColor,
                      mb: 0.8,
                      lineHeight: 1.35,
                    }}
                  >
                    {course.title}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: "0.95rem", color: secondaryTextColor }} />
                    <Typography sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 600 }}>
                      {course.instructor}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 2 }}>
                    <CalendarTodayRoundedIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
                    <Typography sx={{ fontSize: "0.78rem", color: secondaryTextColor, fontWeight: 500 }}>
                      Created: <strong style={{ color: primaryTextColor }}>{course.created}</strong>
                    </Typography>
                  </Box>
                </Box>

                {/* Card Bottom Actions */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1.8,
                    borderTop: `1px solid ${softCardBorder}`,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityRoundedIcon sx={{ fontSize: "0.95rem !important" }} />}
                    onClick={() => setToastMessage(`Curriculum opened: ${course.title}`)}
                    sx={{
                      borderRadius: "6px",
                      borderColor: tealButton,
                      color: tealButton,
                      fontWeight: 700,
                      fontSize: "0.76rem",
                      textTransform: "none",
                      px: 1.8,
                      py: 0.4,
                      "&:hover": {
                        borderColor: tealMain,
                        backgroundColor: "rgba(36, 94, 101, 0.04)",
                      },
                    }}
                  >
                    View Details
                  </Button>

                  <Tooltip title="Delete Course">
                    <IconButton
                      onClick={() => handleDelete(course.id)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        border: "1px solid #EF4444",
                        color: "#EF4444",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(239, 68, 68, 0.08)",
                        },
                      }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: "1.05rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 4. ADD COURSE MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Add Training Course
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Course Title
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Clinical Documentation & Coding"
              value={newCourse.title}
              onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Instructor Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Dr. Usman Rehman"
              value={newCourse.instructor}
              onChange={(e) => setNewCourse({ ...newCourse, instructor: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Category
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Neurology, EVV"
                value={newCourse.category}
                onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Certificate ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. CERT-2026-09"
                value={newCourse.certificateId}
                onChange={(e) => setNewCourse({ ...newCourse, certificateId: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Save Course
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Feedback */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={2500}
        onClose={() => setToastMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastMessage("")}
          severity="success"
          sx={{
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "0.84rem",
            backgroundColor: isDark ? "#064E3B" : "#DCFCE7",
            color: isDark ? "#6EE7B7" : "#15803D",
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseManagement;